import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import {
  getApprovedDevices, addApprovedDevice, toggleApprovedDevice, deleteApprovedDevice,
} from '../../api_service';

// Roles allowed to manage approved devices (mirror backend tms.device.admin-roles).
const ADMIN_ROLES = ['CEO', 'CTO', 'HR'];

const DeviceManagement = () => {
  const navigate = useNavigate();
  const roleName = (sessionStorage.getItem('roleName') || '').toUpperCase();
  const isAdmin = ADMIN_ROLES.includes(roleName);

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mac, setMac] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getApprovedDevices();
      setDevices(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, []); // eslint-disable-line

  const macValid = (v) => /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(v.trim());

  const handleAdd = async () => {
    const m = mac.trim().toUpperCase();
    if (!macValid(m)) { setError('Enter a valid MAC address (e.g. AA:BB:CC:DD:EE:FF)'); return; }
    setSaving(true); setError('');
    try {
      await addApprovedDevice({ mac: m, label: label.trim(), addedBy: sessionStorage.getItem('userName') || roleName });
      setMac(''); setLabel('');
      await load();
    } catch {
      setError('Could not add the device. You may not have permission.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => { try { await toggleApprovedDevice(id); await load(); } catch { /* ignore */ } };
  const handleDelete = async (id) => {
    if (!window.confirm('Remove this device from the allow-list?')) return;
    try { await deleteApprovedDevice(id); await load(); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userName={sessionStorage.getItem('userName') || 'User'} userRole="manager" onLogout={() => navigate('/')} />
      <main className="pt-16 max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Approved Devices</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Machines allowed to access TMS (by MAC). CEO &amp; CTO are exempt and don&apos;t need to be listed.
          </p>
        </div>

        {!isAdmin ? (
          <div className="bg-card border border-border rounded-xl p-10 text-center">
            <Icon name="ShieldAlert" size={40} className="text-error mx-auto mb-3" />
            <p className="text-foreground font-medium">Not authorized</p>
            <p className="text-sm text-muted-foreground mt-1">Only CEO, CTO and HR can manage approved devices.</p>
          </div>
        ) : (
          <>
            {/* Add device */}
            <div className="bg-card border border-border rounded-xl p-5 mb-6">
              <h2 className="text-sm font-semibold text-foreground mb-3">Add a device</h2>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">MAC Address *</label>
                  <Input value={mac} onChange={e => setMac(e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Label (optional)</label>
                  <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Anand — Office PC" />
                </div>
                <Button onClick={handleAdd} disabled={saving} iconName="Plus" iconSize={16}>
                  {saving ? 'Adding…' : 'Add'}
                </Button>
              </div>
              {error && <p className="text-sm text-error mt-2">{error}</p>}
            </div>

            {/* List */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">Allow-list ({devices.length})</span>
                <Button variant="ghost" size="sm" onClick={load} iconName="RefreshCw" iconSize={14}>Refresh</Button>
              </div>
              {loading ? (
                <p className="p-6 text-sm text-muted-foreground text-center">Loading…</p>
              ) : devices.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground text-center">No devices approved yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-5 py-2 font-medium">MAC Address</th>
                      <th className="px-5 py-2 font-medium">Label</th>
                      <th className="px-5 py-2 font-medium">Status</th>
                      <th className="px-5 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {devices.map(d => (
                      <tr key={d.deviceId} className="hover:bg-muted/20">
                        <td className="px-5 py-3 font-mono text-foreground">{d.macAddress}</td>
                        <td className="px-5 py-3 text-foreground">{d.label || <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${d.active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                            {d.active ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleToggle(d.deviceId)}>
                              {d.active ? 'Disable' : 'Enable'}
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(d.deviceId)} iconName="Trash2" iconSize={16} title="Remove" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default DeviceManagement;
